/* ------------------------------------------------------------------ */
/*  Xavior blue palette for white background                           */
/*  Based on #0EA5E9 with indigo shadow for contrast                   */
/* ------------------------------------------------------------------ */

export const COLORS_NORMAL = {
  ringShadow: "rgba(14, 80, 140, 0.18)",
  ringAura: "rgba(14, 145, 220, 0.34)",
  mid: "rgba(14, 145, 220, 0.72)",
  highlight: "rgba(240, 249, 255, 0.98)",
  highlightEdge: "rgba(186, 230, 253, 0.85)",
  haze1: "rgba(224, 242, 254, 0.55)",
  haze2: "rgba(247, 248, 255, 0.96)",
  trail: "rgba(14, 165, 233, ",
};

// Slightly deeper/muted blue for processing state
export const COLORS_PROCESSING = {
  ringShadow: "rgba(10, 60, 120, 0.22)",
  ringAura: "rgba(10, 110, 180, 0.38)",
  mid: "rgba(10, 110, 180, 0.76)",
  highlight: "rgba(220, 238, 250, 0.90)",
  highlightEdge: "rgba(160, 210, 240, 0.80)",
  haze1: "rgba(200, 225, 245, 0.45)",
  haze2: "rgba(235, 242, 252, 0.90)",
  trail: "rgba(10, 130, 200, ",
};

// Active reference — swapped in the tick function
export let COLORS = COLORS_NORMAL;

/** Swap the active palette (processing uses a deeper, muted blue). */
export function setColors(next: typeof COLORS_NORMAL): void {
  COLORS = next;
}

/* ------------------------------------------------------------------ */
/*  Math helpers                                                       */
/* ------------------------------------------------------------------ */

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount;
}

export function wrapAngle(angle: number): number {
  let wrapped = (angle + Math.PI) % (Math.PI * 2);
  if (wrapped < 0) wrapped += Math.PI * 2;
  return wrapped - Math.PI;
}

/* ------------------------------------------------------------------ */
/*  Ring displacement -- localized wave packets that orbit the ring    */
/* ------------------------------------------------------------------ */

export function displacement(theta: number, time: number, energy: number): number {
  const p1 = 0.74 * time + 0.18 * Math.sin(0.7 * time);
  const p2 = p1 + 2.2 + 0.18 * Math.sin(0.43 * time + 0.8);
  const p3 = p1 - 1.45 + 0.12 * Math.sin(0.58 * time + 1.7);

  const localLobe = (
    distance: number,
    width: number,
    amplitude: number,
    frequency: number,
    phase: number,
  ): number => {
    const envelope = Math.exp(-0.5 * Math.pow(distance / width, 2));
    return (
      envelope *
      (amplitude * Math.sin(distance * frequency + phase) +
        amplitude *
          0.28 *
          Math.cos(distance * (frequency * 0.62) - phase * 0.7))
    );
  };

  const scale = 1 + energy * 1.5;
  const d1 = wrapAngle(theta - p1);
  const d2 = wrapAngle(theta - p2);
  const d3 = wrapAngle(theta - p3);

  return (
    localLobe(d1, 0.44, 11.5 * scale, 4.8, -2.4 * time) +
    localLobe(d2, 0.34, 8.0 * scale, 5.1, 2.2 * time) +
    localLobe(d3, 0.24, 5.0 * scale, 5.6, -1.6 * time) +
    0.9 * Math.sin(theta * 2.6 - time * 0.5)
  );
}

/* ------------------------------------------------------------------ */
/*  Build ring path                                                    */
/* ------------------------------------------------------------------ */

export interface RingPath {
  points: { x: number; y: number }[];
  cx: number;
  cy: number;
  radius: number;
}

export function buildPath(
  width: number,
  height: number,
  time: number,
  energy: number,
  amount: number = 1,
  steps: number = 300,
): RingPath {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) * 0.285;
  const points: { x: number; y: number }[] = [];

  for (let i = 0; i <= steps; i += 1) {
    const theta = (Math.PI * 2 * i) / steps;
    const radial = radius + displacement(theta, time, energy) * amount;
    points.push({
      x: cx + Math.cos(theta) * radial,
      y: cy + Math.sin(theta) * radial,
    });
  }

  return { points, cx, cy, radius };
}

/* ------------------------------------------------------------------ */
/*  Stroke a ring path with optional CSS blur filter                   */
/* ------------------------------------------------------------------ */

export function strokePath(
  ctx: CanvasRenderingContext2D,
  points: { x: number; y: number }[],
  lineWidth: number,
  strokeStyle: string,
  blur: number = 0,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x, points[i].y);
  }

  ctx.closePath();
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = strokeStyle;
  ctx.filter = blur ? `blur(${blur}px)` : "none";
  ctx.stroke();
  ctx.restore();
}

/* ------------------------------------------------------------------ */
/*  Paint -- full frame render                                         */
/*  Layered pipeline from ChatGPT VoiceRingButton:                     */
/*    1. Background radial haze                                        */
/*    2. Multiply shadow                                               */
/*    3. Aura glow (blurred)                                           */
/*    4. Ghost trails (8 delayed time samples)                         */
/*    5. Mid ring                                                      */
/*    6. Highlight edge                                                */
/*    7. Bright highlight core                                         */
/* ------------------------------------------------------------------ */

export function paint(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  time: number,
  energy: number,
): void {
  ctx.clearRect(0, 0, width, height);

  const base = buildPath(width, height, time, energy, 0.55);
  const { cx, cy, radius } = base;

  // 1. Background haze
  const glow = ctx.createRadialGradient(
    cx, cy, radius * 0.45,
    cx, cy, radius * 1.65,
  );
  glow.addColorStop(0, COLORS.haze2);
  glow.addColorStop(0.52, COLORS.haze1);
  glow.addColorStop(1, "rgba(255, 255, 255, 0)");

  ctx.save();
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 1.75, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // 2. Shadow (multiply blend for depth on white)
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  strokePath(
    ctx,
    base.points,
    Math.max(6, radius * 0.035),
    COLORS.ringShadow,
    Math.max(3, radius * 0.018),
  );
  ctx.restore();

  // 3. Aura glow
  strokePath(
    ctx,
    base.points,
    Math.max(12, radius * 0.064),
    COLORS.ringAura,
    Math.max(10, radius * 0.09),
  );

  // 4. Ghost trails
  const trailCount = 8;
  for (let i = trailCount; i >= 1; i -= 1) {
    const trailTime = time - i * 0.085;
    const path = buildPath(width, height, trailTime, energy, 1.0);
    const alpha = lerp(0.12, 0.3, (trailCount - i) / (trailCount - 1));
    const lw = lerp(1.0, 2.4, (trailCount - i) / (trailCount - 1));
    strokePath(ctx, path.points, lw, `${COLORS.trail}${alpha.toFixed(3)})`);
  }

  // 5. Mid ring
  strokePath(
    ctx,
    buildPath(width, height, time, energy, 0.36).points,
    Math.max(4, radius * 0.022),
    COLORS.mid,
    Math.max(1.8, radius * 0.01),
  );

  // 6. Highlight edge
  strokePath(
    ctx,
    buildPath(width, height, time, energy, 0.23).points,
    Math.max(2.4, radius * 0.014),
    COLORS.highlightEdge,
    0.6,
  );

  // 7. Bright highlight core
  strokePath(
    ctx,
    buildPath(width, height, time, energy, 0.18).points,
    Math.max(1.7, radius * 0.01),
    COLORS.highlight,
    0.0,
  );
}
